/**
 * Riwayat Saya — Modul Kehadiran Tahap 7 (2026-10-14, user request).
 *
 * Dashboard pribadi: kalender bulanan + legenda, agregat jumlah hari terlambat
 * & total menit, tren jam masuk/pulang vs jadwal, frekuensi/durasi tugas luar
 * & izin pribadi, riwayat lembur (aktual/istirahat/disetujui), catatan perlu
 * koreksi, perbandingan bulan sebelumnya, timeline detail harian.
 *
 * Semua angka = data NYATA dari compute service terpadu (GET /attendance/my-month).
 * Tidak menggambar tren contoh ketika data kosong.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Calendar, Clock, LogIn, LogOut, Coffee, Users, Timer, AlertTriangle,
  CheckCircle2, ChevronLeft, ChevronRight, Info, TrendingUp, TrendingDown,
} from "lucide-react";
import api, { formatDetail } from "@/lib/api";

const STATUS = {
  hadir: { label: "Hadir", bg: "bg-emerald-100", text: "text-emerald-800", dot: "bg-emerald-500" },
  incomplete: { label: "Belum Pulang", bg: "bg-amber-100", text: "text-amber-800", dot: "bg-amber-500" },
  needs_review: { label: "Perlu Peninjauan", bg: "bg-rose-100", text: "text-rose-800", dot: "bg-rose-500" },
  tidak_hadir: { label: "Tidak Hadir", bg: "bg-rose-50", text: "text-rose-700", dot: "bg-rose-300" },
  cuti: { label: "Cuti/Izin", bg: "bg-indigo-100", text: "text-indigo-800", dot: "bg-indigo-500" },
  libur: { label: "Libur", bg: "bg-slate-100", text: "text-slate-600", dot: "bg-slate-400" },
  needs_schedule: { label: "Belum Ada Jadwal", bg: "bg-slate-50", text: "text-slate-500", dot: "bg-slate-300" },
};

function fmtTime(iso, tz = "Asia/Jakarta") {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit", timeZone: tz }); }
  catch { return iso; }
}
function fmtHM(min) {
  if (!min) return "0m";
  const h = Math.floor(min / 60), m = min % 60;
  return h ? `${h}j ${m}m` : `${m}m`;
}
function monthStr(d) { return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`; }
function monthLabel(m) { try { return new Date(m + "-01").toLocaleDateString("id-ID", { month: "long", year: "numeric" }); } catch { return m; } }

/* Kalender */
function CalendarGrid({ days, onPick, picked }) {
  const first = days[0];
  if (!first) return null;
  const firstDate = new Date(first.work_date);
  const dow = (firstDate.getDay() + 6) % 7;  // Senin=0
  const cells = Array(dow).fill(null).concat(days);
  while (cells.length % 7) cells.push(null);
  const DAYS = ["Sen", "Sel", "Rab", "Kam", "Jum", "Sab", "Min"];
  return (
    <div className="border border-slate-200 bg-white p-3" data-testid="att-riwayat-cal">
      <div className="grid grid-cols-7 gap-1 mb-1">
        {DAYS.map((d) => <div key={d} className="text-[9px] uppercase tracking-wider text-slate-500 text-center">{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((c, i) => {
          if (!c) return <div key={i} />;
          const s = STATUS[c.status] || STATUS.needs_schedule;
          const isPicked = picked === c.work_date;
          const day = parseInt(c.work_date.slice(-2), 10);
          return (
            <button key={c.work_date} type="button" onClick={() => onPick(c.work_date)}
              data-testid={`att-riwayat-cal-${c.work_date}`}
              className={`h-16 border p-1 text-left transition hover:border-[#FF5A1F] ${isPicked ? "border-[#FF5A1F] ring-1 ring-[#FF5A1F]" : "border-slate-200"} ${s.bg}`}>
              <div className="flex items-center justify-between">
                <span className={`text-xs font-semibold ${s.text}`}>{day}</span>
                <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
              </div>
              {c.late_minutes > 0 && <div className="text-[9px] text-rose-700 mt-0.5">⏰ {c.late_minutes}m</div>}
              {c.overtime_actual_net_minutes > 0 && <div className="text-[9px] text-orange-700">OT {fmtHM(c.overtime_actual_net_minutes)}</div>}
              {c.needs_review && <div className="text-[9px] text-rose-700">!</div>}
            </button>
          );
        })}
      </div>
      <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2 text-[9px] uppercase tracking-wider text-slate-600" data-testid="att-riwayat-legend">
        {Object.entries(STATUS).map(([k, s]) => (
          <span key={k} className="inline-flex items-center gap-1"><span className={`w-2 h-2 rounded-full ${s.dot}`} /> {s.label}</span>
        ))}
      </div>
    </div>
  );
}

/* Tren jam masuk/pulang vs jadwal — bar sederhana per hari kerja. */
function TrendPanel({ days }) {
  const working = days.filter((d) => (d.shift || {}).start_time && d.clock_in_at);
  if (working.length === 0) {
    return (
      <div className="border border-slate-200 bg-slate-50 p-3 text-[11px] text-slate-500 italic" data-testid="att-riwayat-trend-empty">
        Tren jam masuk/pulang akan muncul setelah ada data absensi aktual pada bulan ini.
      </div>
    );
  }
  const maxDiff = Math.max(60, ...working.map((d) => Math.abs(d.late_minutes || 0)), 30);
  return (
    <div className="border border-slate-200 bg-white p-3 space-y-1" data-testid="att-riwayat-trend">
      <div className="font-display text-[11px] uppercase tracking-wider text-[#0B1D3A] flex items-center gap-2"><TrendingUp size={12} /> Tren Jam Masuk vs Jadwal</div>
      <div className="space-y-0.5 text-[10px]">
        {working.slice(-14).map((d) => {
          const diff = d.late_minutes || 0;
          const pct = Math.min(100, (Math.abs(diff) / maxDiff) * 100);
          return (
            <div key={d.work_date} className="flex items-center gap-2" data-testid={`att-riwayat-trend-${d.work_date}`}>
              <span className="w-16 tabular-nums text-slate-500">{d.work_date.slice(-5)}</span>
              <span className="w-10 tabular-nums text-slate-700">{fmtTime(d.clock_in_at, d.timezone)}</span>
              <div className="flex-1 bg-slate-100 h-2 relative">
                <div className={`absolute top-0 h-2 ${diff > 0 ? "bg-rose-400" : "bg-emerald-400"}`} style={{ width: `${pct}%` }} />
              </div>
              <span className={`w-14 tabular-nums text-right ${diff > 0 ? "text-rose-700 font-semibold" : "text-emerald-700"}`}>{diff > 0 ? `+${diff}m` : "tepat"}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* Timeline detail harian */
function DayTimeline({ day }) {
  if (!day) return null;
  const tz = day.timezone;
  return (
    <div className="border border-slate-200 bg-white p-4 space-y-3" data-testid={`att-riwayat-timeline-${day.work_date}`}>
      <div className="flex items-center justify-between">
        <div>
          <div className="font-display text-sm text-[#0B1D3A]">{new Date(day.work_date).toLocaleDateString("id-ID", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</div>
          <div className="text-[10px] text-slate-500">{(day.location || {}).name || "—"} · {day.schedule_name || "Tanpa jadwal"}</div>
        </div>
        <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 border ${STATUS[day.status]?.bg} ${STATUS[day.status]?.text}`}>{STATUS[day.status]?.label}</span>
      </div>
      {day.was_corrected && (
        <div className="border border-amber-200 bg-amber-50 px-3 py-1.5 text-[10px] text-amber-800">
          Catatan dikoreksi (bukti asli tetap tersimpan).
          {day.original_clock_in_at && <span> Masuk asli: {fmtTime(day.original_clock_in_at, tz)}.</span>}
          {day.original_clock_out_at && <span> Pulang asli: {fmtTime(day.original_clock_out_at, tz)}.</span>}
        </div>
      )}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
        <div className="border border-slate-200 p-2"><div className="text-[9px] uppercase tracking-wider text-slate-500">Masuk</div><div className="font-semibold tabular-nums"><LogIn size={10} className="inline mr-1" />{fmtTime(day.clock_in_at, tz)}</div></div>
        <div className="border border-slate-200 p-2"><div className="text-[9px] uppercase tracking-wider text-slate-500">Pulang</div><div className="font-semibold tabular-nums"><LogOut size={10} className="inline mr-1" />{fmtTime(day.clock_out_at, tz)}</div></div>
        <div className="border border-slate-200 p-2"><div className="text-[9px] uppercase tracking-wider text-slate-500">Di Lokasi</div><div className="font-semibold tabular-nums">{fmtHM(day.at_location_minutes)}</div></div>
        <div className="border border-slate-200 p-2"><div className="text-[9px] uppercase tracking-wider text-slate-500">Terlambat</div><div className={`font-semibold tabular-nums ${day.late_minutes > 0 ? "text-rose-700" : "text-emerald-700"}`}>{fmtHM(day.late_minutes)}</div></div>
      </div>
      {day.breaks?.length > 0 && (
        <div data-testid="att-riwayat-breaks">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Sesi Keluar</div>
          <table className="w-full text-[11px] border border-slate-200">
            <thead className="bg-slate-50 text-[9px] uppercase tracking-wider text-slate-600"><tr><th className="px-2 py-1 text-left">Jenis</th><th className="px-2 py-1 text-left">Mulai</th><th className="px-2 py-1 text-left">Kembali</th><th className="px-2 py-1 text-right">Durasi</th></tr></thead>
            <tbody>
              {day.breaks.map((b, i) => (
                <tr key={i} className="border-t border-slate-100">
                  <td className="px-2 py-1">{b.kind === "office_duty" ? <span className="text-sky-700"><Users size={9} className="inline" /> Tugas Luar</span> : <span className="text-amber-700"><Coffee size={9} className="inline" /> Pribadi</span>}</td>
                  <td className="px-2 py-1 tabular-nums">{fmtTime(b.actual_start_at, tz)}</td>
                  <td className="px-2 py-1 tabular-nums">{fmtTime(b.actual_return_at, tz)}</td>
                  <td className="px-2 py-1 text-right tabular-nums">{fmtHM(b.duration_minutes)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {day.overtimes?.length > 0 && (
        <div data-testid="att-riwayat-ot">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Lembur</div>
          <table className="w-full text-[11px] border border-slate-200">
            <thead className="bg-slate-50 text-[9px] uppercase tracking-wider text-slate-600"><tr><th className="px-2 py-1 text-left">Mulai</th><th className="px-2 py-1 text-left">Selesai</th><th className="px-2 py-1 text-right">Aktual</th><th className="px-2 py-1 text-right">Istirahat</th><th className="px-2 py-1 text-right">Disetujui</th><th className="px-2 py-1 text-left">Status</th></tr></thead>
            <tbody>
              {day.overtimes.map((o, i) => (
                <tr key={i} className="border-t border-slate-100">
                  <td className="px-2 py-1 tabular-nums">{fmtTime(o.actual_start_at, tz)}</td>
                  <td className="px-2 py-1 tabular-nums">{fmtTime(o.actual_end_at, tz)}</td>
                  <td className="px-2 py-1 text-right tabular-nums">{fmtHM(o.net_actual_minutes)}</td>
                  <td className="px-2 py-1 text-right tabular-nums text-slate-500">{fmtHM(o.break_minutes)}</td>
                  <td className="px-2 py-1 text-right tabular-nums font-semibold">{fmtHM(o.approved_minutes)}</td>
                  <td className="px-2 py-1 text-[9px] uppercase tracking-wider">{o.approval_status || o.session_status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {day.pending_corrections?.length > 0 && (
        <div className="border border-amber-200 bg-amber-50 p-2 text-[10px] text-amber-800" data-testid="att-riwayat-pending">
          <AlertTriangle size={10} className="inline mr-1" />
          {day.pending_corrections.length} pengajuan koreksi menunggu — lihat tab Pengajuan & Persetujuan.
        </div>
      )}
    </div>
  );
}

/* Perbandingan bulan sebelumnya */
function Comparison({ cur, prev }) {
  const metrics = [
    ["hari_hadir", "Hari Hadir"],
    ["hari_terlambat", "Hari Terlambat"],
    ["menit_terlambat", "Total Menit Terlambat", true],
    ["menit_tugas_luar", "Durasi Tugas Luar", true],
    ["menit_izin_pribadi", "Durasi Izin Pribadi", true],
    ["menit_lembur_disetujui", "Lembur Disetujui", true],
  ];
  return (
    <div className="border border-slate-200 bg-white p-3" data-testid="att-riwayat-comparison">
      <div className="font-display text-[11px] uppercase tracking-wider text-[#0B1D3A] mb-2">Perbandingan Bulan Sebelumnya</div>
      <table className="w-full text-xs">
        <thead className="text-[9px] uppercase tracking-wider text-slate-500"><tr><th className="text-left">Metrik</th><th className="text-right">Bulan Ini</th><th className="text-right">Bulan Lalu</th><th className="text-right">Perubahan</th></tr></thead>
        <tbody>
          {metrics.map(([k, label, isMin]) => {
            const now = cur?.[k] || 0;
            const old = prev?.[k] || 0;
            const diff = now - old;
            const bad = (k.startsWith("menit_terlambat") || k.startsWith("hari_terlambat")) ? diff > 0 : diff < 0;
            return (
              <tr key={k} className="border-t border-slate-100">
                <td className="py-1">{label}</td>
                <td className="py-1 text-right tabular-nums font-semibold">{isMin ? fmtHM(now) : now}</td>
                <td className="py-1 text-right tabular-nums text-slate-500">{isMin ? fmtHM(old) : old}</td>
                <td className={`py-1 text-right tabular-nums text-[10px] ${diff === 0 ? "text-slate-400" : bad ? "text-rose-700" : "text-emerald-700"}`}>
                  {diff === 0 ? "—" : `${diff > 0 ? "+" : ""}${isMin ? fmtHM(Math.abs(diff)) : Math.abs(diff)}`}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/* Panel utama */
export default function RiwayatSayaPanel() {
  const [month, setMonth] = useState(monthStr(new Date()));
  const [data, setData] = useState(null);
  const [picked, setPicked] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    setLoading(true); setErr(null);
    api.get(`/attendance/my-month?month=${month}`).then((r) => { setData(r.data); setPicked(null); })
      .catch((e) => { setErr(formatDetail(e.response?.data?.detail) || "Gagal memuat riwayat."); setData(null); })
      .finally(() => setLoading(false));
  }, [month]);
  useEffect(() => { load(); }, [load]);

  const pickedDay = useMemo(() => data?.current?.days?.find((d) => d.work_date === picked) || null, [data, picked]);

  const shift = (delta) => {
    const [y, m] = month.split("-").map(Number);
    const d = new Date(y, m - 1 + delta, 1);
    setMonth(monthStr(d));
  };

  if (err) return <div className="border border-rose-200 bg-rose-50 px-3 py-2 text-[11px] text-rose-700" data-testid="att-riwayat-err">{err}</div>;
  if (loading && !data) return <div className="p-4 text-xs text-slate-500">Memuat riwayat…</div>;
  if (!data) return null;
  const totals = data.current.totals;
  const hasAny = (data.current.days || []).some((d) => d.event || d.breaks.length || d.overtimes.length);

  return (
    <div className="space-y-3" data-testid="att-riwayat-panel">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h2 className="font-display text-base text-[#0B1D3A] flex items-center gap-2"><Calendar size={16} /> Riwayat Saya — {monthLabel(month)}</h2>
        <div className="flex items-center gap-1">
          <button type="button" onClick={() => shift(-1)} className="p-1.5 border border-slate-200 hover:bg-slate-50" data-testid="att-riwayat-prev"><ChevronLeft size={12} /></button>
          <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="h-7 text-xs border border-slate-200 px-2" data-testid="att-riwayat-month" />
          <button type="button" onClick={() => shift(1)} className="p-1.5 border border-slate-200 hover:bg-slate-50" data-testid="att-riwayat-next"><ChevronRight size={12} /></button>
        </div>
      </div>
      {!hasAny && (
        <div className="border border-sky-200 bg-sky-50 px-3 py-2 text-[11px] text-sky-800 flex items-start gap-2" data-testid="att-riwayat-empty">
          <Info size={14} className="mt-0.5 shrink-0" />
          <span>Belum ada data absensi pada bulan ini. Kalender di bawah menampilkan status jadwal (libur/kerja/belum ada jadwal) — tidak dibuat grafik contoh.</span>
        </div>
      )}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-2" data-testid="att-riwayat-totals">
        <StatCard label="Hari Hadir" value={totals.hari_hadir} />
        <StatCard label="Hari Terlambat" value={totals.hari_terlambat} tone="rose" />
        <StatCard label="Menit Terlambat" value={fmtHM(totals.menit_terlambat)} tone="rose" />
        <StatCard label="Tugas Luar" value={fmtHM(totals.menit_tugas_luar)} tone="sky" />
        <StatCard label="Izin Pribadi" value={fmtHM(totals.menit_izin_pribadi)} tone="amber" />
        <StatCard label="Lembur Disetujui" value={fmtHM(totals.menit_lembur_disetujui)} tone="orange" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <CalendarGrid days={data.current.days} onPick={setPicked} picked={picked} />
        <div className="space-y-3">
          <TrendPanel days={data.current.days} />
          <Comparison cur={totals} prev={data.previous?.totals} />
        </div>
      </div>
      {picked && pickedDay && <DayTimeline day={pickedDay} />}
      {!picked && hasAny && <div className="text-[10px] text-slate-500 italic text-center py-2">Klik tanggal pada kalender untuk melihat timeline detail harian.</div>}
    </div>
  );
}

function StatCard({ label, value, tone }) {
  const cls = tone === "rose" ? "border-rose-200" : tone === "sky" ? "border-sky-200" : tone === "amber" ? "border-amber-200" : tone === "orange" ? "border-orange-200" : "border-slate-200";
  return (
    <div className={`border ${cls} bg-white p-2`} data-testid={`att-riwayat-stat-${label.replace(/\s+/g, "-").toLowerCase()}`}>
      <div className="text-[9px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className="font-display text-lg tabular-nums text-[#0B1D3A]">{value}</div>
    </div>
  );
}
